import {MaskControl} from '@wandb/weave/common/components/MediaCard';
import Color from 'color';
import React, {useCallback, useMemo} from 'react';
import {isNumber} from 'vega';

import {segmentationMaskColor} from '../../util/media';
import ColorPicker from '../ColorPicker';
import {MaskVisibilityToggle} from './MaskVisibilityToggle';

type MaskColorProps = {
  maskKey: string;
  classLabel: string;
  maskControl?: MaskControl;
  classID: string;
  onUpdateColor?: (color: string) => void;
};

const MaskClassColor = ({
  maskControl,
  maskKey,
  classLabel,
  classID,
  onUpdateColor,
}: MaskColorProps) => {
  const defaultMaskColor = useMemo(() => {
    const maskColor =
      classID === 'all' ? [0, 0, 0] : segmentationMaskColor(Number(classID));
    return [maskColor[0], maskColor[1], maskColor[2]];
  }, [classID]);

  let maskColor = maskControl?.rgb ?? defaultMaskColor;
  if (maskColor.some(color => !isNumber(color))) {
    maskColor = defaultMaskColor;
  }
  const backgroundColor = `rgba(${maskColor[0]}, ${maskColor[1]}, ${
    maskColor[2]
  }, ${maskControl?.opacity ?? 1})`;

  return (
    <div
      className="text-right"
      aria-label={`Mask color ${maskKey} ${classLabel}`}>
      <ColorPicker
        activeColor={backgroundColor}
        setColor={onUpdateColor}
        // HACK: aligns the box's caret with the swatch
        containerStyle={{left: '14px'}}
        position="top right"
      />
    </div>
  );
};

type MaskClassProps = {
  classID: string;
  className: string;
  mediaKey: string;
  maskKey: string;
  getMaskControl: (configPath: Array<string>) => MaskControl | undefined;
  update: (newControl: Partial<MaskControl>, configPath: Array<string>) => void;
};

const MaskClassComponent = ({
  mediaKey,
  maskKey,
  classID,
  className,
  getMaskControl,
  update,
}: MaskClassProps) => {
  const configPath = useMemo(
    () => ['toggles', mediaKey, maskKey, classID],
    [mediaKey, maskKey, classID]
  );
  const maskControl = useMemo(
    () => getMaskControl(configPath),
    [getMaskControl, configPath]
  );
  const parentMaskControl = useMemo(
    () =>
      getMaskControl(['toggles', mediaKey, classID ? maskKey : 'all', 'all']),
    [getMaskControl, mediaKey, maskKey, classID]
  );
  const disabled = (maskControl?.disabled ??
    parentMaskControl?.disabled) as boolean;

  const onToggleVisibility = useCallback(
    () => update({disabled: !disabled}, configPath),
    [update, configPath, disabled]
  );

  const onUpdateColor = useCallback(
    (color: string) => {
      const chosenColor = Color(color);
      return update(
        {
          opacity: chosenColor.alpha(),
          rgb: [chosenColor.red(), chosenColor.green(), chosenColor.blue()],
        },
        configPath
      );
    },
    [update, configPath]
  );

  return (
    <div className="flex w-full flex-row items-center justify-between px-4 py-4">
      <MaskVisibilityToggle
        title={`${maskKey}:${className}`}
        disabled={disabled}
        onToggleVisibility={onToggleVisibility}>
        <p className="text-sm" data-test="mask-class-name">
          {className}
        </p>
      </MaskVisibilityToggle>
      <MaskClassColor
        maskControl={maskControl}
        maskKey={maskKey}
        classID={classID}
        classLabel={className}
        onUpdateColor={onUpdateColor}
      />
    </div>
  );
};

export const MaskClass = React.memo(MaskClassComponent);
